/**
 * Species/Lexikon Search Enhancement
 * Adds metaobject species profiles to predictive search results
 */
(function() {
  'use strict';

  // Shared global state so multiple sections/scripts can reuse the index
  const GLOBAL_STATE_KEY = '__silberkraftLexikonIndexState';
  const globalState = window[GLOBAL_STATE_KEY] || (window[GLOBAL_STATE_KEY] = {
    data: null,
    promise: null,
    sitemap: null,
    sitemapPromise: null,
    dynamicImageCache: {},
    pendingImageLoads: new Set()
  });

  // State
  let searchInput = null;
  let searchResults = null;
  let lastSearchTerm = '';
  let isRendering = false;
  let updateTimer = null;
  let didInit = false;

  // Normalize string for comparison (handle umlauts)
  function normalizeStr(str) {
    if (!str) return '';
    // Canonicalize different umlaut spellings to the same form so that
    // "käfer", "kaefer" and "kafer" can all match each other.
    return String(str)
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u');
  }

  function isArrayOfObjects(value) {
    return Array.isArray(value);
  }

  function uniqueByUrl(items) {
    const seen = new Set();
    const out = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      // Extrahiere Handle aus URL für bessere Deduplizierung
      let key = '';
      if (it && it.url) {
        const url = String(it.url);
        // Extrahiere den letzten Teil der URL als Handle
        const parts = url.replace(/\/$/, '').split('/');
        const handle = parts[parts.length - 1] || '';
        // Verwende Handle als Key für Deduplizierung (unabhängig vom Pfad)
        key = handle.toLowerCase() || url;
      } else {
        key = JSON.stringify(it);
      }
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(it);
    }
    return out;
  }

  function getLexikonPathPrefix() {
    // Default matches the URLs in `sitemap_metaobject_pages_*.xml`
    return '/pages/lexikon/';
  }

  function handleFromUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      return parts[parts.length - 1] || '';
    } catch (e) {
      return '';
    }
  }

  function displayFromHandle(handle) {
    if (!handle) return '';
    return String(handle)
      .replace(/-/g, ' ')
      // IMPORTANT: do NOT auto-convert "ae/oe/ue" to umlauts here.
      // Handles may contain Latin terms (e.g. "paecilomyces") where "ae" is not an umlaut.
      .replace(/\b\w/g, function(m) { return m.toUpperCase(); });
  }

  // Optional fallback: load sitemap urls so we can still surface lexikon pages
  // even if the JSON index wasn't generated/updated yet.
  function loadLexikonSitemap() {
    if (globalState.sitemapPromise) return globalState.sitemapPromise;

    const sitemapUrl = '/sitemap_metaobject_pages_1.xml';

    globalState.sitemapPromise = fetch(sitemapUrl, { credentials: 'same-origin' })
      .then(function(res) {
        if (!res.ok) throw new Error('Failed to load sitemap: ' + res.status);
        return res.text();
      })
      .then(function(xmlText) {
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlText, 'application/xml');
        const locNodes = xml.getElementsByTagName('loc');
        const prefix = getLexikonPathPrefix();

        const items = [];
        for (var i = 0; i < locNodes.length; i++) {
          const loc = (locNodes[i].textContent || '').trim();
          if (!loc) continue;
          try {
            const u = new URL(loc, window.location.origin);
            if (!u.pathname.startsWith(prefix)) continue;
            const handle = handleFromUrl(u.href);
            items.push({
              handle: handle,
              url: u.pathname // keep relative
            });
          } catch (e) {
            // ignore
          }
        }

        globalState.sitemap = items;
        return items;
      })
      .catch(function() {
        globalState.sitemap = [];
        return [];
      });

    return globalState.sitemapPromise;
  }

  // Load lexikon index from theme asset (preferred) with fallback to inline data.
  function loadLexikonIndex() {
    if (globalState.promise) return globalState.promise;

    const fallbackInline = isArrayOfObjects(window.speciesSearchData) ? window.speciesSearchData : [];
    const indexUrl = window.lexikonIndexUrl || '/assets/lexikon-index.json';

    globalState.promise = fetch(indexUrl, { credentials: 'same-origin' })
      .then(function(res) {
        if (!res.ok) throw new Error('Failed to load lexikon index: ' + res.status);
        return res.json();
      })
      .then(function(json) {
        const assetData = isArrayOfObjects(json) ? json : [];
        globalState.data = uniqueByUrl([].concat(assetData, fallbackInline));
        return globalState.data;
      })
      .catch(function() {
        globalState.data = uniqueByUrl([].concat(fallbackInline));
        return globalState.data;
      });

    return globalState.promise;
  }

  function getLexikonData() {
    if (isArrayOfObjects(globalState.data)) return globalState.data;
    return isArrayOfObjects(window.speciesSearchData) ? window.speciesSearchData : [];
  }

  // Search species by term
  function searchSpecies(term, limit) {
    if (!term || term.length < 2) return [];
    var normalizedTerm = normalizeStr(term);
    var lowerTerm = term.toLowerCase();

    var data = getLexikonData();

    var matches = data.filter(function(species) {
      var nameDe = species.name_de || '';
      var scientific = species.scientific_name || '';

      // Try both normalized and direct comparison
      var nameMatch = nameDe.toLowerCase().indexOf(lowerTerm) !== -1 ||
                      normalizeStr(nameDe).indexOf(normalizedTerm) !== -1;
      var scientificMatch = scientific.toLowerCase().indexOf(lowerTerm) !== -1 ||
                            normalizeStr(scientific).indexOf(normalizedTerm) !== -1;

      return nameMatch || scientificMatch;
    });

    var max = typeof limit === 'number' ? limit : 5;
    matches = matches.slice(0, max);

    // If we still have room, add sitemap-only matches (best-effort display name).
    if (matches.length < max) {
      var sitemap = Array.isArray(globalState.sitemap) ? globalState.sitemap : [];
      if (sitemap.length) {
        var remaining = max - matches.length;

        var extra = sitemap.filter(function(it) {
          var h = it && it.handle ? String(it.handle) : '';
          if (!h) return false;
          // Use normalized comparison only (handles umlaut variations like "brotkäfer" vs "brotkaefer")
          return normalizeStr(h).indexOf(normalizedTerm) !== -1;
        }).slice(0, remaining).map(function(it) {
          return {
            name_de: displayFromHandle(it.handle),
            scientific_name: '',
            url: it.url
          };
        });

        matches = uniqueByUrl([].concat(matches, extra)).slice(0, max);
      }
    }

    return matches;
  }

  // Handle link click
  function handleLinkClick(e) {
    e.preventDefault();
    e.stopPropagation();
    var url = this.getAttribute('href');
    if (url) {
      window.location.href = url;
    }
  }

  // Get image from mapping or cache
  function getImageForSpecies(species) {
    // PRIORITÄT 0: main_image_url direkt aus den Daten (neues taxonomy_tree Format)
    if (species.main_image_url) {
      return species.main_image_url;
    }

    var images = window.speciesSearchImages || {};
    var url = species.url || '';
    var name = (species.name_de || '').toLowerCase().trim();
    var scientific = (species.scientific_name || '').toLowerCase().trim();

    // Extract handle from URL
    var handle = '';
    if (url.includes('/pages/lexikon/')) {
      handle = url.split('/pages/lexikon/')[1];
      if (handle) handle = handle.replace(/\/$/, '').toLowerCase();
    }

    // Try different keys
    if (handle && images[handle]) return images[handle];
    if (name && images[name]) return images[name];
    if (scientific && images[scientific]) return images[scientific];

    // Check dynamic cache
    if (handle && globalState.dynamicImageCache[handle]) {
      return globalState.dynamicImageCache[handle];
    }

    return null;
  }

  // Load image dynamically from species page
  function loadImageForHandle(handle) {
    if (!handle || globalState.dynamicImageCache[handle] !== undefined || globalState.pendingImageLoads.has(handle)) {
      return Promise.resolve(globalState.dynamicImageCache[handle] || null);
    }

    globalState.pendingImageLoads.add(handle);

    return fetch('/pages/lexikon/' + handle, {
      method: 'GET',
      headers: { 'Accept': 'text/html' }
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Failed to load');
      return response.text();
    })
    .then(function(html) {
      // Try to find species-hero__image
      var imgMatch = html.match(/<img[^>]+class="[^"]*species-hero__image[^"]*"[^>]+src="([^"]+)"/i);
      if (!imgMatch) {
        imgMatch = html.match(/<img[^>]+src="([^"]+)"[^>]+class="[^"]*species-hero__image[^"]*"/i);
      }
      if (!imgMatch) {
        var heroImgMatch = html.match(/<img[^>]*class="[^"]*species-hero__image[^"]*"[^>]*>/i);
        if (heroImgMatch) {
          var srcMatch = heroImgMatch[0].match(/src="([^"]+)"/i);
          if (srcMatch) imgMatch = [null, srcMatch[1]];
        }
      }

      if (imgMatch && imgMatch[1]) {
        var imgUrl = imgMatch[1];
        // Resize to 80px for search thumbnails
        if (!imgUrl.includes('width=')) {
          imgUrl += (imgUrl.includes('?') ? '&' : '?') + 'width=80';
        } else {
          imgUrl = imgUrl.replace(/width=\d+/, 'width=80');
        }
        globalState.dynamicImageCache[handle] = imgUrl;
        return imgUrl;
      }

      // Fallback: any CDN image
      var cdnMatch = html.match(/src="(https:\/\/[^"]*cdn[^"]*shop[^"]*files[^"]*(?:jpg|jpeg|png|webp)[^"]*)"/i);
      if (cdnMatch && cdnMatch[1] && !cdnMatch[1].toLowerCase().includes('logo')) {
        var imgUrl = cdnMatch[1];
        if (!imgUrl.includes('width=')) {
          imgUrl += (imgUrl.includes('?') ? '&' : '?') + 'width=80';
        } else {
          imgUrl = imgUrl.replace(/width=\d+/, 'width=80');
        }
        globalState.dynamicImageCache[handle] = imgUrl;
        return imgUrl;
      }

      globalState.dynamicImageCache[handle] = null;
      return null;
    })
    .catch(function() {
      globalState.dynamicImageCache[handle] = null;
      return null;
    })
    .finally(function() {
      globalState.pendingImageLoads.delete(handle);
    });
  }

  // Update placeholder with loaded image
  function updatePlaceholderWithImage(placeholder, imgUrl) {
    if (!placeholder || !imgUrl) return;

    var img = document.createElement('img');
    img.src = imgUrl;
    img.alt = '';
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
    img.loading = 'lazy';

    placeholder.innerHTML = '';
    placeholder.appendChild(img);
    placeholder.classList.remove('search-flydown__lexikon-image--placeholder');
  }

  // Render species results
  function renderSpeciesResults(term) {
    if (isRendering || term === lastSearchTerm) return;

    lastSearchTerm = term;
    isRendering = true;

    // Remove existing
    var existing = document.querySelector('.search-flydown__lexikon');
    if (existing) existing.parentNode.removeChild(existing);

    var results = searchSpecies(term, 5);

    if (!results.length) {
      isRendering = false;
      return;
    }

    // Build HTML
    var html = '<h3 class="search-flydown__group-title">Lexikon</h3><ul class="search-flydown__group-list search-flydown__lexikon-list">';

    for (var i = 0; i < results.length; i++) {
      var species = results[i];
      var name = species.name_de || species.scientific_name || '';
      var scientific = species.scientific_name || '';
      var url = species.url || '#';

      // Extract handle for lazy loading
      var handle = '';
      if (url.includes('/pages/lexikon/')) {
        handle = url.split('/pages/lexikon/')[1];
        if (handle) handle = handle.replace(/\/$/, '').toLowerCase();
      }

      // Try to get image from mapping
      var imageUrl = getImageForSpecies(species);

      html += '<li class="search-flydown__list-item search-flydown__lexikon-item">';
      html += '<a href="' + url + '" class="search-flydown__lexikon-link">';

      if (imageUrl) {
        // We have an image - show it directly
        html += '<span class="search-flydown__lexikon-image"><img src="' + imageUrl + '" alt="" style="width:100%;height:100%;object-fit:cover;" loading="lazy"></span>';
      } else {
        // No image yet - show placeholder with data-handle for lazy loading
        html += '<span class="search-flydown__lexikon-image search-flydown__lexikon-image--placeholder" data-handle="' + handle + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg></span>';
      }

      html += '<span class="search-flydown__lexikon-text">';
      html += '<span class="search-flydown__lexikon-name">' + name + '</span>';
      if (scientific && scientific !== name) {
        html += '<span class="search-flydown__lexikon-scientific">' + scientific + '</span>';
      }
      html += '</span></a></li>';
    }

    html += '</ul>';

    // Create container
    var container = document.createElement('div');
    container.className = 'search-flydown__lexikon';
    container.innerHTML = html;

    // Add click handlers
    var links = container.querySelectorAll('.search-flydown__lexikon-link');
    for (var j = 0; j < links.length; j++) {
      links[j].addEventListener('click', handleLinkClick);
    }

    // Insert into DOM
    var innerDiv = searchResults.querySelector(':scope > div');
    if (innerDiv) {
      innerDiv.appendChild(container);
    } else {
      searchResults.appendChild(container);
    }

    // Hide no results message
    var noResults = searchResults.querySelector('.search-flydown__no-results');
    if (noResults) {
      noResults.style.display = 'none';
    }

    // Recalculate flydown height to include lexikon results (fixes overflow/scrollbar issue)
    var flydown = searchResults.closest('[data-live-search-flydown]');
    if (flydown) {
      var visibleContainer = flydown.querySelector(':scope > .visible');
      if (visibleContainer) {
        var newHeight = visibleContainer.scrollHeight;
        flydown.style.setProperty('--open-height', newHeight + 'px');
      }
    }

    // Lazy load images for placeholders
    var placeholders = container.querySelectorAll('.search-flydown__lexikon-image--placeholder[data-handle]');
    placeholders.forEach(function(placeholder) {
      var handle = placeholder.dataset.handle;
      if (handle) {
        loadImageForHandle(handle).then(function(imgUrl) {
          if (imgUrl) {
            updatePlaceholderWithImage(placeholder, imgUrl);
          }
        });
      }
    });

    isRendering = false;
  }

  // Schedule update with debounce
  function scheduleUpdate() {
    if (updateTimer) clearTimeout(updateTimer);

    updateTimer = setTimeout(function() {
      if (!searchInput) return;

      var term = searchInput.value.trim();
      if (term.length >= 2) {
        // Load index only when the user actually searches (keeps initial page-load light).
        loadLexikonIndex().then(function() {
          // If the user changed the term while the index was loading, skip stale render.
          var currentTerm = searchInput ? searchInput.value.trim() : '';
          if (currentTerm !== term) return;

          // Lazy-load sitemap only when the user actually searches (avoid huge XML on every page view).
          if (!globalState.sitemapPromise) {
            loadLexikonSitemap().then(function() {
              // Force rerender once sitemap is available (fills gaps for entries not in the index).
              lastSearchTerm = '';
              renderSpeciesResults(searchInput.value.trim());
            });
          }

          renderSpeciesResults(term);
        });

      } else {
        lastSearchTerm = '';
        var existing = document.querySelector('.search-flydown__lexikon');
        if (existing) existing.parentNode.removeChild(existing);
      }
    }, 250);
  }

  // Initialize
  function init() {
    if (didInit) return;

    searchInput = document.querySelector('[data-live-search-input]');
    searchResults = document.querySelector('[data-live-search-results]');

    if (!searchInput || !searchResults) {
      // Retry after short delay (header/search can hydrate late).
      setTimeout(init, 300);
      return;
    }

    didInit = true;

    // Listen for input/focus (index will load on demand in scheduleUpdate).
    searchInput.addEventListener('input', scheduleUpdate);
    searchInput.addEventListener('focus', scheduleUpdate);

    // Observe DOM changes from predictive search
    var observer = new MutationObserver(function() {
      if (!isRendering) {
        lastSearchTerm = ''; // Force re-render
        scheduleUpdate();
      }
    });

    observer.observe(searchResults, {
      childList: true,
      subtree: false
    });
  }

  // Public API for other scripts (e.g. search results page)
  window.SilberkraftLexikonSearch = window.SilberkraftLexikonSearch || {
    load: function() { return Promise.all([loadLexikonIndex(), loadLexikonSitemap()]); },
    search: function(term, limit) { return searchSpecies(term, limit); },
    normalize: normalizeStr
  };

  // Start when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
